import { fetchQuery } from 'relay-runtime';
import { fg } from '@confluence/feature-gating';
import RelayLocalStorageQuery, {
	type LocalStorageQuery as RelayLocalStorageQueryType,
} from './contract-queries/__generated__/LocalStorageQuery.graphql.ts';
import { cfetch } from '@confluence/network';
import { SSRMeasures } from '@confluence/action-measures/entry-points/SSRMeasures.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';

import { LocalStorageQuery } from './LocalStorageQueryRaw.ts';
import { PERSISTED_KEYS_ON_SERVER } from './LocalStorageKeys.ts';
import { getKeysForLocalStorageQuery } from './storageHelpers.ts';
import type { LocalStorageQueryVariables } from './__types__/LocalStorageQuery.ts';
import type { StorageManagerInitContext } from './StorageManagerInitContext.ts';

type RemoteLocalStorageStringValue = {
	key: string;
	value: string;
};

const LOCAL_STORAGE_ITEM_LENGTH_METRIC = 'localStorageMaxItemValueLength';
const LOCAL_STORAGE_TYPE = 'localStorage';
const persistedRemoteStorageKeyEntries = Object.entries(PERSISTED_KEYS_ON_SERVER);
const scheduledRemoteLocalStorageValues = new WeakSet<unknown[]>();

function scheduleRemoteLocalStorageMaxItemLengthMetric(values: unknown) {
	if (
		!Array.isArray(values) ||
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		Boolean(process.env.REACT_SSR) ||
		scheduledRemoteLocalStorageValues.has(values) ||
		typeof window.requestIdleCallback !== 'function'
	) {
		return;
	}

	scheduledRemoteLocalStorageValues.add(values);
	// eslint-disable-next-line compat/compat -- Guarded above; unsupported browsers skip this diagnostic.
	window.requestIdleCallback(() => submitRemoteLocalStorageMaxItemLengthMetric(values));
}

function submitRemoteLocalStorageMaxItemLengthMetric(values: unknown) {
	if (
		!Array.isArray(values) ||
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		Boolean(process.env.REACT_SSR) // skipping in SSR because getMonitoringClient().gauge is a no-op in SSR
	) {
		return;
	}

	const longestItem = values.reduce<{
		storageKeyTag: string;
		valueLength: number;
	} | null>((currentLongestItem, entry: unknown) => {
		if (!isRemoteLocalStorageStringValue(entry)) {
			return currentLongestItem;
		}

		const storageKeyTag = getPersistedRemoteStorageKeyTag(entry.key);
		if (!storageKeyTag) {
			return currentLongestItem;
		}

		const valueLength = entry.value.length;
		if (!currentLongestItem || valueLength > currentLongestItem.valueLength) {
			return { storageKeyTag, valueLength };
		}

		return currentLongestItem;
	}, null);

	if (!longestItem) {
		return;
	}

	getMonitoringClient().gauge(LOCAL_STORAGE_ITEM_LENGTH_METRIC, longestItem.valueLength, [
		`storageKey:${longestItem.storageKeyTag}`,
		'source:remote_fetch',
		`storageType:${LOCAL_STORAGE_TYPE}`,
	]);
}

function isRemoteLocalStorageStringValue(entry: unknown): entry is RemoteLocalStorageStringValue {
	if (!entry || typeof entry !== 'object') {
		return false;
	}

	const { key, value } = entry as Record<string, unknown>;
	return typeof key === 'string' && typeof value === 'string';
}

function getPersistedRemoteStorageKeyTag(remoteKey: string): string | null {
	const persistedStorageKeyEntry = persistedRemoteStorageKeyEntries.find(([, storageKey]) =>
		remoteKey.endsWith(`.${storageKey}`),
	);

	return persistedStorageKeyEntry?.[0] ?? null;
}

export async function getRemoteLocalStorage({
	userId,
	isLicensed,
	regionSteeringHint,
}: StorageManagerInitContext): Promise<any> {
	if (window['__REMOTE_LOCAL_STORAGE__']) {
		// This value can be returned from SSR result, so measure it when the client first accesses it.
		// Keep this diagnostic off the critical path and schedule it at most once per payload.
		scheduleRemoteLocalStorageMaxItemLengthMetric(window['__REMOTE_LOCAL_STORAGE__']);
		return window['__REMOTE_LOCAL_STORAGE__'];
	}

	const regionHint = regionSteeringHint || window['GLOBAL_AGG_REGION_STEERING_HINT'];

	const URI = '/gateway/api/graphql/slow';
	return (window['__REMOTE_LOCAL_STORAGE__'] = SSRMeasures.run(
		'ssr-app/prepare/preloadQuery/fetch:LocalStorageQuery',
		async () => {
			let result = null;
			try {
				if (isLicensed && userId) {
					const variables: LocalStorageQueryVariables = {
						stringKeys: getKeysForLocalStorageQuery(userId),
					};

					const specialHeaders: Record<string, string> = {
						'atl-attribution': JSON.stringify({
							product: 'Confluence Cloud',
							service: 'confluence-frontend',
						}),
					};
					if (regionHint) {
						specialHeaders['Atl-Edge-Region-Hint'] = regionHint;
					}

					if (fg('confluence_storage_manager_native_relay')) {
						const environment = window.GLOBAL_RELAY_ENV;
						if (!environment) {
							getMonitoringClient().submitError(
								new Error('Missing Relay environment while loading user preferences'),
								{ attribution: 'unknown' },
							);
						} else {
							const data = await fetchQuery<RelayLocalStorageQueryType>(
								environment,
								RelayLocalStorageQuery,
								variables,
								{ networkCacheConfig: { force: true, metadata: { headers: specialHeaders } } },
							).toPromise();
							result = data?.localStorage?.stringValues;
						}
					} else {
						const res = await cfetch(`${URI}?q=LocalStorageQuery`, {
							method: 'POST',
							headers: {
								'X-APOLLO-OPERATION-NAME': 'LocalStorageQuery',
								'Content-Type': 'application/json',
								...specialHeaders,
							},
							body: JSON.stringify({
								operationName: 'LocalStorageQuery',
								query: LocalStorageQuery,
								variables,
							}),
						});
						if (res.ok) {
							const { data } = await res.json();
							result = data?.localStorage?.stringValues;
						}
					}
				}
			} catch {}
			return (window['__REMOTE_LOCAL_STORAGE__'] = result);
		},
	));
}
